import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  createCodingPacket,
  createNewRole,
  createPrescreeningQues,
  editJobDetails,
  getCodingPackets,
  getJobDetails,
  updateCodingPacket,
} from 'src/services/role.service';
import { useSnackbar } from 'notistack';
import { AuthContext } from 'src/auth/context/jwt';
import { getCreditDetails, getOrganizationId } from 'src/services/user.service';
import { LoadingScreen } from 'src/components/loading-screen';
import { useParams, useRouter } from 'src/routes/hooks';
import { getCandidatesSummary } from 'src/services/candidate.service';
import { formatDate } from 'src/utils/formatDate';
import {
  editInterviewQuestions,
  enableTwoWayInterview,
} from 'src/services/interview.service';
import { JobCreationContext } from './job-creation-context';

const JOB_CREATION_STATES = {
  JOB_DETAILS: {
    label: 'Job Details',
    value: 'JOB_DETAILS',
  },
  AUTOPILOT_WORKFLOW: {
    label: 'Autopilot Setup',
    value: 'AUTOPILOT_WORKFLOW',
  },
  LAUNCH: { label: 'Launch', value: 'LAUNCH' },
};

const initialState = {
  currentState: JOB_CREATION_STATES.JOB_DETAILS.value,
  loading: true,
  JOB_CREATION_STATES,
  mode: '',
  canEdit: true,
  jobId: '',
  jobApplyLink: '',
  jobTitle: '',
  jobExperience: '',
  jobSkills: [],
  jobPrimarySkill: '',
  jobDescription: '',
  clickedStage: 'PRESCREENING',
  jobDetails: null,
  preScreeningQuestions: [
    {
      title: 'Notice period',
      question:
        'What is the minimum notice period you require before joining (in days)?',
      responseType: 'NUMERIC',
      booleanAnswer: 'YES',
      min: '0',
      max: '',
      isQualifyingQuestion: true,
    },
  ],
  preferredQualifyingCriterias: [],
  interviewQuestions: [],
  codingAssessments: [],
  needCodingAssessment: false,
  interactiveInterview: {
    showInteractiveInterview: false,
    criteriaText: '',
  },
  interviewDeadline: '7',
  officeLocation: '',
  workLocation: '',
  employmentType: '',
  salaryCurrency: '',
  minSalary: '',
  maxSalary: '',
  revalidateFunction: null,
  errors: {},
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return initialState;
  }
  if (action.type === 'SET_CLICKED_STAGE') {
    return { ...state, clickedStage: action.payload.clickedStage };
  }
  if (action.type === 'SET_CAN_EDIT') {
    return { ...state, canEdit: action.payload.canEdit };
  }
  if (action.type === 'SET_LOADING') {
    return { ...state, loading: action.payload.loading };
  }
  if (action.type === 'SET_MODE') {
    return { ...state, mode: action.payload.mode };
  }
  if (action.type === 'SET_CURRENT_STATE') {
    return { ...state, currentState: action.payload.selectedState };
  }
  if (action.type === 'SET_JOB_ID') {
    return { ...state, jobId: action.payload.jobId };
  }
  if (action.type === 'SET_JOB_APPLY_LINK') {
    return { ...state, jobApplyLink: action.payload.jobApplyLink };
  }
  if (action.type === 'SET_JOB_TITLE') {
    return { ...state, jobTitle: action.payload.jobTitle };
  }
  if (action.type === 'SET_JOB_EXPERIENCE') {
    return { ...state, jobExperience: action.payload.jobExperience };
  }
  if (action.type === 'SET_JOB_SKILLS') {
    return {
      ...state,
      jobPrimarySkill: action.payload.jobSkills?.length
        ? action.payload.jobSkills[0]
        : '',
      jobSkills: action.payload.jobSkills,
    };
  }
  if (action.type === 'SET_JOB_PRIMARY_SKILL') {
    const jobSkills = [...state.jobSkills];
    const index = jobSkills.findIndex(
      skill => skill === action.payload.jobPrimarySkill,
    );

    if (index !== -1) {
      jobSkills.splice(index, 1);
      jobSkills.unshift(action.payload.jobPrimarySkill);
    }

    return {
      ...state,
      jobSkills,
      jobPrimarySkill: action.payload.jobPrimarySkill,
    };
  }
  if (action.type === 'SET_JOB_DESCRIPTION') {
    return { ...state, jobDescription: action.payload.jobDescription };
  }
  if (action.type === 'SET_JOB_DETAILS') {
    return { ...state, jobDetails: action.payload.jobDetails };
  }
  if (action.type === 'SET_PRE_SCREENING_QUESTIONS') {
    return {
      ...state,
      preScreeningQuestions: action.payload.preScreeningQuestions,
    };
  }
  if (action.type === 'SET_PREFERRED_QUALIFYING_CRITERIAS') {
    return {
      ...state,
      preferredQualifyingCriterias: action.payload.preferredQualifyingCriterias,
    };
  }
  if (action.type === 'SET_INTERVIEW_QUESTIONS') {
    return {
      ...state,
      interviewQuestions: action.payload.interviewQuestions,
    };
  }
  if (action.type === 'SET_CODING_ASSESSMENTS') {
    return {
      ...state,
      codingAssessments: action.payload.codingAssessments,
    };
  }
  if (action.type === 'SET_INTERVIEW_DEADLINE') {
    return {
      ...state,
      interviewDeadline: action.payload.interviewDeadline,
    };
  }
  if (action.type === 'SET_NEED_CODING_ASSESSMENT') {
    return {
      ...state,
      needCodingAssessment: action.payload.needCodingAssessment,
    };
  }
  if (action.type === 'SET_INTERACTIVE_INTERVIEW') {
    return {
      ...state,
      interactiveInterview: action.payload.interactiveInterview,
    };
  }
  if (action.type === 'ADD_PRE_SCREENING_QUESTION') {
    return {
      ...state,
      preScreeningQuestions: [
        ...state.preScreeningQuestions,
        action.payload.question,
      ],
    };
  }

  if (action.type === 'SET_OFFICE_LOCATION') {
    return {
      ...state,
      officeLocation: action.payload.officeLocation,
    };
  }

  if (action.type === 'SET_WORK_LOCATION') {
    return {
      ...state,
      workLocation: action.payload.workLocation,
    };
  }

  if (action.type === 'SET_EMPLOYMENT_TYPE') {
    return {
      ...state,
      employmentType: action.payload.employmentType,
    };
  }

  if (action.type === 'SET_SALARY_CURRENCY') {
    return {
      ...state,
      salaryCurrency: action.payload.salaryCurrency,
    };
  }

  if (action.type === 'SET_MIN_SALARY') {
    return {
      ...state,
      minSalary: action.payload.minSalary,
    };
  }

  if (action.type === 'SET_MAX_SALARY') {
    return {
      ...state,
      maxSalary: action.payload.maxSalary,
    };
  }

  if (action.type === 'SET_REVALIDATE_FUNCTION') {
    return {
      ...state,
      revalidateFunction: action.payload.revalidateFunction,
    };
  }

  if (action.type === 'SET_ERRORS') {
    return {
      ...state,
      errors: action.payload.errors,
    };
  }

  return state;
};

export default function JobCreationProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSavingJob, setIsSavingJob] = useState(false);

  // hooks
  const { id: paramJobId } = useParams();
  const context = useContext(AuthContext);
  const { user } = context;
  const orgId = getOrganizationId(context.user);
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  // setters
  const setClickedStage = useCallback(clickedStage => {
    dispatch({
      type: 'SET_CLICKED_STAGE',
      payload: {
        clickedStage,
      },
    });
  }, []);

  const setLoading = useCallback(loading => {
    dispatch({
      type: 'SET_LOADING',
      payload: {
        loading,
      },
    });
  }, []);

  const setCanEdit = useCallback(canEdit => {
    dispatch({
      type: 'SET_CAN_EDIT',
      payload: {
        canEdit,
      },
    });
  }, []);

  const setMode = useCallback(mode => {
    dispatch({
      type: 'SET_MODE',
      payload: {
        mode,
      },
    });
  }, []);

  const setCurrentState = useCallback(selectedState => {
    dispatch({
      type: 'SET_CURRENT_STATE',
      payload: {
        selectedState,
      },
    });
  }, []);

  const setJobId = useCallback(jobId => {
    dispatch({
      type: 'SET_JOB_ID',
      payload: {
        jobId,
      },
    });
  }, []);

  const setJobApplyLink = useCallback(jobApplyLink => {
    dispatch({
      type: 'SET_JOB_APPLY_LINK',
      payload: {
        jobApplyLink,
      },
    });
  }, []);

  const setJobTitle = useCallback(jobTitle => {
    dispatch({
      type: 'SET_JOB_TITLE',
      payload: {
        jobTitle,
      },
    });
  }, []);

  const setJobExperience = useCallback(jobExperience => {
    dispatch({
      type: 'SET_JOB_EXPERIENCE',
      payload: {
        jobExperience,
      },
    });
  }, []);

  const setJobSkills = useCallback(jobSkills => {
    dispatch({
      type: 'SET_JOB_SKILLS',
      payload: {
        jobSkills,
      },
    });
  }, []);

  const setJobPrimarySkill = useCallback(jobPrimarySkill => {
    dispatch({
      type: 'SET_JOB_PRIMARY_SKILL',
      payload: {
        jobPrimarySkill,
      },
    });
  }, []);

  const setJobDescription = useCallback(jobDescription => {
    dispatch({
      type: 'SET_JOB_DESCRIPTION',
      payload: {
        jobDescription,
      },
    });
  }, []);

  const setJobDetails = useCallback(jobDetails => {
    dispatch({
      type: 'SET_JOB_DETAILS',
      payload: {
        jobDetails,
      },
    });
  }, []);

  const setPreScreeningQuestions = useCallback(preScreeningQuestions => {
    dispatch({
      type: 'SET_PRE_SCREENING_QUESTIONS',
      payload: {
        preScreeningQuestions,
      },
    });
  }, []);

  const setPreferredQualifyingCriterias = useCallback(
    preferredQualifyingCriterias => {
      dispatch({
        type: 'SET_PREFERRED_QUALIFYING_CRITERIAS',
        payload: {
          preferredQualifyingCriterias,
        },
      });
    },
    [],
  );

  const setInterviewQuestions = useCallback(interviewQuestions => {
    dispatch({
      type: 'SET_INTERVIEW_QUESTIONS',
      payload: {
        interviewQuestions,
      },
    });
  }, []);

  const setCodingAssessments = useCallback(codingAssessments => {
    dispatch({
      type: 'SET_CODING_ASSESSMENTS',
      payload: {
        codingAssessments,
      },
    });
  }, []);

  const setNeedCodingAssessment = useCallback(needCodingAssessment => {
    dispatch({
      type: 'SET_NEED_CODING_ASSESSMENT',
      payload: {
        needCodingAssessment,
      },
    });
  }, []);

  const setInteractiveInterview = useCallback(interactiveInterview => {
    dispatch({
      type: 'SET_INTERACTIVE_INTERVIEW',
      payload: {
        interactiveInterview,
      },
    });
  }, []);

  const setInterviewDeadline = useCallback(interviewDeadline => {
    dispatch({
      type: 'SET_INTERVIEW_DEADLINE',
      payload: {
        interviewDeadline,
      },
    });
  }, []);

  const addPreScreeningQuestion = useCallback(question => {
    dispatch({
      type: 'ADD_PRE_SCREENING_QUESTION',
      payload: {
        question,
      },
    });
  }, []);

  const setOfficeLocation = useCallback(officeLocation => {
    dispatch({
      type: 'SET_OFFICE_LOCATION',
      payload: {
        officeLocation,
      },
    });
  }, []);

  const setWorkLocation = useCallback(workLocation => {
    dispatch({
      type: 'SET_WORK_LOCATION',
      payload: {
        workLocation,
      },
    });
  }, []);

  const setEmploymentType = useCallback(employmentType => {
    dispatch({
      type: 'SET_EMPLOYMENT_TYPE',
      payload: {
        employmentType,
      },
    });
  }, []);

  const setSalaryCurrency = useCallback(salaryCurrency => {
    dispatch({
      type: 'SET_SALARY_CURRENCY',
      payload: {
        salaryCurrency,
      },
    });
  }, []);

  const setMinSalary = useCallback(minSalary => {
    dispatch({
      type: 'SET_MIN_SALARY',
      payload: {
        minSalary,
      },
    });
  }, []);

  const setMaxSalary = useCallback(maxSalary => {
    dispatch({
      type: 'SET_MAX_SALARY',
      payload: {
        maxSalary,
      },
    });
  }, []);

  const setRevalidateFunction = useCallback(revalidateFunction => {
    dispatch({
      type: 'SET_REVALIDATE_FUNCTION',
      payload: {
        revalidateFunction,
      },
    });
  }, []);

  const setErrors = useCallback(errors => {
    dispatch({
      type: 'SET_ERRORS',
      payload: {
        errors,
      },
    });
  }, []);

  // functions
  const saveJobDetails = useCallback(async () => {
    try {
      setIsSavingJob(true);

      if (state.currentState === JOB_CREATION_STATES.JOB_DETAILS.value) {
        if (!state.jobTitle)
          enqueueSnackbar({
            message: 'Enter job name',
            variant: 'error',
          });
        else if (state.jobId) {
          const isValid = await state.revalidateFunction();

          if (isValid) {
            // job
            const payload = {};

            payload.job_description = state.jobDescription;

            if (state.canEdit) {
              payload.name = state.jobTitle;
              payload.min_experience = state.jobExperience;
              payload.skills = state.skills;
            }

            const response = await editJobDetails(payload, orgId, state.jobId);
            if (response.success) {
              enqueueSnackbar('Job details saved successfully');
            } else throw new Error('Error while saving job details');
          } else {
            enqueueSnackbar('Please fill all the required fields correctly', {
              variant: 'error',
            });
          }
        } else if (!state.jobId) {
          const isValid = await state.revalidateFunction();

          if (isValid) {
            const roleData = {
              name: state.jobTitle,
              skills: state.jobSkills,
              min_experience: state.jobExperience,
              job_description: state.jobDescription,
            };

            createNewRole(roleData, orgId)
              .then(_data => {
                dispatch({
                  type: 'SET_JOB_ID',
                  payload: _data.body.job_opening_id,
                });

                dispatch({
                  type: 'SET_JOB_DETAILS',
                  payload: _data.body,
                });

                enqueueSnackbar('Job details saved successfully');
              })
              .catch(err => {
                console.log('creation provider bugs: ', err);
                enqueueSnackbar({
                  message: 'Error while saving job details',
                  variant: 'error',
                });
              });
          } else {
            enqueueSnackbar('Please fill all the required fields correctly', {
              variant: 'error',
            });
          }
        }
      } else if (
        state.currentState === JOB_CREATION_STATES.AUTOPILOT_WORKFLOW.value
      ) {
        const isValid = await state.revalidateFunction();

        if (isValid) {
          const responses = {
            preScreeningQuestions: {
              success: null,
            },
            preferredCriteria: {
              success: null,
            },
            primarySkillResponse: {
              success: null,
            },
            interviewQuestionsResponse: {
              success: null,
            },
            interviewDeadlineResponse: {
              success: null,
            },
            codingAssessmentResponse: {
              success: null,
            },
            interactiveInterviewResponse: {
              success: null,
            },
          };

          // pre-screening questions
          if (state.canEdit) {
            const payloadPreScreeningQuestions = {};

            payloadPreScreeningQuestions.questions =
              state.preScreeningQuestions.map(item => {
                const newQues = {
                  title: item.title,
                  text: item.question,
                  response_type: item.responseType,
                  is_qualifying_question: item.isQualifyingQuestion,
                };

                if (item.id) {
                  newQues.question_id = item.id;
                }

                if (item.responseType === 'BOOLEAN') {
                  newQues.response_parameters = {
                    IDEAL_ANSWER: item.booleanAnswer,
                  };
                }

                if (item.responseType === 'NUMERIC') {
                  newQues.response_parameters = {
                    MIN_VALUE: item.min,
                    MAX_VALUE: item.max,
                  };
                }

                if (item.responseType === 'TEXT') {
                  newQues.response_parameters = {
                    IDEAL_ANSWER: '-',
                  };
                }

                return newQues;
              });

            responses.preScreeningQuestions = await createPrescreeningQues(
              payloadPreScreeningQuestions,
              orgId,
              state.jobId,
            );
          }

          // criteria list
          if (state.canEdit) {
            const payload = {};

            const criteriaList = state.preferredQualifyingCriterias.map(
              criteria => {
                if (criteria.criteria) {
                  return criteria.criteria;
                }
                return null;
              },
            );

            payload.criteria_list = criteriaList;

            responses.preferredCriteria = await editJobDetails(
              payload,
              orgId,
              state.jobId,
            );
          }

          // primary skill
          if (state.canEdit) {
            const payload = {
              skills: state.jobSkills,
            };

            responses.primarySkillResponse = await editJobDetails(
              payload,
              orgId,
              state.jobId,
            );
          }

          // interview questions
          if (state.canEdit) {
            const interviewQuestionsRequest = state.interviewQuestions.map(
              (question, index) => {
                const existingQuestion = state.jobDetails.questions[index];

                if (!existingQuestion) {
                  return {
                    text: question.question,
                    skills: [],
                    sl_no: index + 1,
                  };
                }

                const newQuestion = {
                  ...existingQuestion,
                  text: question.question,
                  updated_at: formatDate(new Date()),
                };

                return newQuestion;
              },
            );

            responses.interviewQuestionsResponse = await editInterviewQuestions(
              orgId,
              state.jobId,
              { questions: interviewQuestionsRequest },
            );
          }

          // interview deadline
          if (state.canEdit) {
            responses.interviewDeadlineResponse = await editJobDetails(
              {
                ai_interview_validity: state.interviewDeadline,
              },
              orgId,
              state.jobId,
            );
          }

          // coding assessments
          if (state.canEdit) {
            const codingAssessmentPromises = state.codingAssessments.map(
              async codingAssessment => {
                const packetId = codingAssessment.id;
                const codingQuestions = codingAssessment.questions;
                const codingLanguages = codingAssessment.languages;

                const payload = {
                  interview_name: 'Coding Assessment',
                  coding_questions: codingQuestions.map((question, index) => {
                    const newQuestion = {
                      question_text: question.text,
                      input_output_list: question.testCases.map(testCase => ({
                        input: testCase.input,
                        output: testCase.output,
                      })),
                      time_limit_minutes: question.timeLimit,
                    };

                    return newQuestion;
                  }),
                  allowed_coding_languages: codingLanguages,
                };

                if (packetId) {
                  return updateCodingPacket(orgId, packetId, payload);
                }

                return createCodingPacket(orgId, state.jobId, payload);
              },
            );

            const codingAssessmentResponses = await Promise.all(
              codingAssessmentPromises,
            );

            const codingAssessmentResponse = codingAssessmentResponses.reduce(
              (acc, response) => {
                if (response.success) {
                  acc.success = true;
                }
                return acc;
              },
            );

            responses.codingAssessmentResponse = codingAssessmentResponse;
          }

          // interactive interview
          if (state.canEdit) {
            const payload = {
              showInteractiveInterview:
                state.interactiveInterview.showInteractiveInterview,
              criteriaText: state.interactiveInterview.criteriaText,
            };

            responses.interactiveInterviewResponse =
              await enableTwoWayInterview(orgId, state.jobId, payload);
          }

          if (
            responses.preScreeningQuestions.success !== null &&
            responses.preferredCriteria.success !== null &&
            responses.primarySkillResponse.success !== null &&
            responses.interviewQuestionsResponse.success !== null &&
            responses.interviewDeadlineResponse.success !== null &&
            responses.codingAssessmentResponse.success !== null &&
            responses.interactiveInterviewResponse.success !== null
          ) {
            if (
              responses.preferredCriteria.success &&
              responses.preScreeningQuestions.success &&
              responses.primarySkillResponse.success &&
              responses.interviewQuestionsResponse.success &&
              responses.interviewDeadlineResponse.success &&
              responses.codingAssessmentResponse.success &&
              responses.interactiveInterviewResponse.success
            ) {
              enqueueSnackbar('Autopilot Workflow saved successfully');
            } else throw new Error('Error while saving Autopilot Workflow');
          } else {
            enqueueSnackbar('Autopilot Workflow saved successfully');
          }
        } else {
          enqueueSnackbar('Please fill all the required fields correctly', {
            variant: 'error',
          });
        }
      }
      // else if (state.currentState === JOB_CREATION_STATES.LAUNCH.value) {
      //   const payload = {
      //     office_location: state.officeLocation,
      //     work_location: state.workLocation,
      //     employment_type: state.employmentType,
      //     salary_currency: state.salaryCurrency,
      //     salary_min: state.minSalary,
      //     salary_max: state.maxSalary,
      //   };

      //   const response = await editJobDetails(payload, orgId, state.jobId);

      //   if (response.success) {
      //     enqueueSnackbar('Job details saved successfully');
      //   } else throw new Error('Error while saving job details');
      // }
    } catch (error) {
      console.log(error);
      enqueueSnackbar({
        message: 'Error while saving job details',
        variant: 'error',
      });
    } finally {
      setIsSavingJob(false);
    }
  }, [state, enqueueSnackbar, orgId]);

  // memo
  const memoizedValue = useMemo(
    () => ({
      ...state,
      isSavingJob,
      JOB_CREATION_STATES,
      setClickedStage,
      setLoading,
      setCanEdit,
      setMode,
      setCurrentState,
      setJobId,
      setJobApplyLink,
      setJobTitle,
      setJobExperience,
      setJobSkills,
      setJobPrimarySkill,
      setJobDescription,
      setJobDetails,
      setPreScreeningQuestions,
      setPreferredQualifyingCriterias,
      setInterviewQuestions,
      setNeedCodingAssessment,
      setCodingAssessments,
      setInteractiveInterview,
      setInterviewDeadline,
      addPreScreeningQuestion,
      saveJobDetails,
      setOfficeLocation,
      setWorkLocation,
      setEmploymentType,
      setSalaryCurrency,
      setMinSalary,
      setMaxSalary,
      setRevalidateFunction,
      setErrors,
    }),
    [
      state,
      isSavingJob,
      setClickedStage,
      setLoading,
      setCanEdit,
      setMode,
      setCurrentState,
      setJobId,
      setJobApplyLink,
      setJobTitle,
      setJobExperience,
      setJobSkills,
      setJobPrimarySkill,
      setJobDescription,
      setJobDetails,
      setPreScreeningQuestions,
      setPreferredQualifyingCriterias,
      setInterviewQuestions,
      setNeedCodingAssessment,
      setCodingAssessments,
      setInteractiveInterview,
      setInterviewDeadline,
      addPreScreeningQuestion,
      saveJobDetails,
      setOfficeLocation,
      setWorkLocation,
      setEmploymentType,
      setSalaryCurrency,
      setMinSalary,
      setMaxSalary,
      setRevalidateFunction,
      setErrors,
    ],
  );

  useEffect(() => {
    const initialize = async () => {
      try {
        if (state.mode === 'CREATE' && state.jobId === '') {
          let credit = null;
          const creditResponse = await getCreditDetails(orgId);
          if (creditResponse && creditResponse.body) {
            credit = creditResponse.body.available_credit;
          }
          if (credit !== null && credit < 1) {
            router.push('/dashboard/jobs?status=ACTIVE&pricing=true');
          }
        } else if (state.mode === 'EDIT') {
          const candidatesSummaryResponse = await getCandidatesSummary(
            orgId,
            paramJobId,
          );
          if (candidatesSummaryResponse.success) {
            const totalCount =
              candidatesSummaryResponse.body.candidate_group_map.APPLIED +
              candidatesSummaryResponse.body.candidate_group_map.AI_INTERVIEW +
              candidatesSummaryResponse.body.candidate_group_map
                .IN_PERSON_INTERVIEW +
              candidatesSummaryResponse.body.candidate_group_map.HIRED +
              candidatesSummaryResponse.body.candidate_group_map.NEED_REVIEW +
              candidatesSummaryResponse.body.candidate_group_map.REJECTED;

            dispatch({
              type: 'SET_CAN_EDIT',
              payload: {
                canEdit: totalCount === 0,
              },
            });
          }

          const res = await getJobDetails(orgId, paramJobId);

          const codingInterviewResponse = await getCodingPackets(
            orgId,
            paramJobId,
          );

          if (codingInterviewResponse.success) {
            const codingPackets = codingInterviewResponse.body;

            const _assessments = codingPackets.map(packet => {
              const _questions = Object.keys(packet.coding_questions).map(
                questionNo => {
                  const question = packet.coding_questions[questionNo];

                  return {
                    text: question.question_text,
                    testCases: question.input_output_list.map(testCase => ({
                      input: testCase.input,
                      output: testCase.output,
                    })),
                    timeLimit: question.time_limit_minutes,
                  };
                },
              );

              const _name = packet.packet_name;
              const _languages = packet.allowed_coding_languages;
              const id = packet.coding_packet_id;

              return {
                id,
                name: _name,
                questions: _questions,
                languages: _languages,
              };
            });

            dispatch({
              type: 'SET_CODING_ASSESSMENTS',
              payload: {
                codingAssessments: _assessments,
              },
            });
          }

          dispatch({
            type: 'SET_JOB_ID',
            payload: {
              jobId: paramJobId,
            },
          });
          dispatch({
            type: 'SET_JOB_TITLE',
            payload: {
              jobTitle: res.body.name,
            },
          });
          dispatch({
            type: 'SET_JOB_EXPERIENCE',
            payload: {
              jobExperience: `${res.body.min_experience}`,
            },
          });
          dispatch({
            type: 'SET_JOB_SKILLS',
            payload: {
              jobSkills: res.body.skills,
            },
          });
          dispatch({
            type: 'SET_JOB_PRIMARY_SKILL',
            payload: {
              jobPrimarySkill: res.body?.skills.length
                ? res.body?.skills[0]
                : '',
            },
          });
          dispatch({
            type: 'SET_JOB_DESCRIPTION',
            payload: {
              jobDescription: res.body?.job_description,
            },
          });
          dispatch({
            type: 'SET_JOB_DETAILS',
            payload: {
              jobDetails: res.body,
            },
          });

          const preScreeningQuestions = res.body.pre_screen_questions?.map(
            item => {
              const newQues = {
                id: item.question_id,
                title: item.title,
                question: item.text,
                responseType: item.response_type,
                min: undefined,
                max: undefined,
                booleanAnswer: 'YES',
                isQualifyingQuestion: item.is_qualifying_question,
              };

              if (item.response_type === 'BOOLEAN') {
                newQues.booleanAnswer = item.response_parameters.IDEAL_ANSWER;
              }

              if (item.response_type === 'NUMERIC') {
                newQues.min = item.response_parameters.MIN_VALUE;
                newQues.max = item.response_parameters.MAX_VALUE;
              }

              return newQues;
            },
          );

          dispatch({
            type: 'SET_PRE_SCREENING_QUESTIONS',
            payload: {
              preScreeningQuestions: preScreeningQuestions || [],
            },
          });

          const preferredQualifyingCriterias = res.body.criteria_list?.map(
            criteria => ({
              criteria,
            }),
          );

          dispatch({
            type: 'SET_PREFERRED_QUALIFYING_CRITERIAS',
            payload: {
              preferredQualifyingCriterias: preferredQualifyingCriterias || [],
            },
          });

          const interviewQuestions = res.body?.questions?.map(question => ({
            question: question.text,
          }));

          dispatch({
            type: 'SET_INTERVIEW_QUESTIONS',
            payload: {
              interviewQuestions: interviewQuestions || [],
            },
          });

          dispatch({
            type: 'SET_INTERVIEW_DEADLINE',
            payload: {
              interviewDeadline: res.body.ai_interview_validity || '',
            },
          });

          dispatch({
            type: 'SET_NEED_CODING_ASSESSMENT',
            payload: {
              needCodingAssessment: res?.body?.need_coding_assessment || false,
            },
          });

          dispatch({
            type: 'SET_INTERACTIVE_INTERVIEW',
            payload: {
              interactiveInterview: {
                showInteractiveInterview:
                  res.body?.two_way_interview_enabled || false,
                criteriaText: res.body?.two_way_interview_data?.interview_requirements || '',
              },
            },
          });

          dispatch({
            type: 'SET_JOB_APPLY_LINK',
            payload: {
              jobApplyLink: res.body.public_apply_link
                ? `${process.env.REACT_APP_APPLY_JOB_BASE_URL}${res.body.public_apply_link}`
                : '',
            },
          });

          dispatch({
            type: 'SET_OFFICE_LOCATION',
            payload: {
              officeLocation: res.body.office_location || '',
            },
          });

          dispatch({
            type: 'SET_WORK_LOCATION',
            payload: {
              workLocation: res.body.work_location || '',
            },
          });

          dispatch({
            type: 'SET_EMPLOYMENT_TYPE',
            payload: {
              employmentType: res.body.employment_type || '',
            },
          });

          dispatch({
            type: 'SET_SALARY_CURRENCY',
            payload: {
              salaryCurrency: res.body.salary_currency || '',
            },
          });

          dispatch({
            type: 'SET_MIN_SALARY',
            payload: {
              minSalary: res.body.salary_min || '',
            },
          });

          dispatch({
            type: 'SET_MAX_SALARY',
            payload: {
              maxSalary: res.body.salary_max || '',
            },
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        dispatch({
          type: 'SET_LOADING',
          payload: {
            loading: false,
          },
        });
      }
    };

    if (state.mode) {
      initialize();
    }
  }, [orgId, paramJobId, router, state.jobId, state.mode]);

  useEffect(() => {
    if (paramJobId) {
      dispatch({
        type: 'SET_MODE',
        payload: {
          mode: 'EDIT',
        },
      });
    } else if (user && user.user_role === 'ADMIN') {
      dispatch({
        type: 'SET_MODE',
        payload: {
          mode: 'CREATE',
        },
      });
    } else {
      router.replace('/401');
    }
  }, [paramJobId, router, user]);

  return (
    <JobCreationContext.Provider value={memoizedValue}>
      {state.loading ? <LoadingScreen loadingText="Loading..." /> : children}
    </JobCreationContext.Provider>
  );
}

JobCreationProvider.propTypes = {
  children: PropTypes.node,
};
